import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { useUploadManager } from "@/context/UploadManagerContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, Upload, Trash2, Save, Rocket, Copy, Check, DollarSign,
  Eye, Download, Gift, Video, Image as ImageIcon, ExternalLink, Play, Film, X,
  GripVertical, Lock, CheckSquare,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-white/40">{label}</span>
        <Icon className={`h-4 w-4 ${accent || "text-[#00F0FF]"}`} />
      </div>
      <p className="font-display text-2xl font-bold mt-3">{value}</p>
    </div>
  );
}

export default function EventEditor() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const uploadMgr = useUploadManager();
  const canReorder = !!user && user.tier !== "starter";
  const COMP_MAX = { starter: 5, pro: 20 };
  const compMax = user ? COMP_MAX[user.tier] : undefined; // undefined = unlimited
  const [event, setEvent] = useState(null);
  const [videos, setVideos] = useState([]);
  const dragIndex = useRef(null);
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState({ title: "", description: "", event_date: "", location: "", complimentary_downloads: 5, unlock_all: false });
  const [savingDetails, setSavingDetails] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [copied, setCopied] = useState(false);
  const [preview, setPreview] = useState(null);
  const [pendingFiles, setPendingFiles] = useState(null);
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [acceptingAgreement, setAcceptingAgreement] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [flashIds, setFlashIds] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedPrice, setSelectedPrice] = useState("10");
  const pendingDeletes = useRef({});

  const [bulkPrice, setBulkPrice] = useState("10");
  const [dragOver, setDragOver] = useState(false);
  const coverRef = useRef(null);
  const videoRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${eventId}`);
      setEvent(data);
      setVideos((data.videos || []).filter((v) => !pendingDeletes.current[v.id]));
      setDetails({
        title: data.title || "", description: data.description || "",
        event_date: data.event_date || "", location: data.location || "",
        complimentary_downloads: data.complimentary_downloads ?? 5,
        unlock_all: !!data.unlock_all,
      });
    } catch {
      toast.error("Event not found");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [eventId, navigate]);

  useEffect(() => { load(); }, [load]);

  // Append clips as background uploads complete (works even after navigating back).
  useEffect(() => {
    const unsub = uploadMgr.registerEventListener(eventId, (video) => {
      setVideos((prev) => (prev.some((v) => v.id === video.id) ? prev : [...prev, video]));
    });
    return unsub;
  }, [eventId, uploadMgr]);

  const saveDetails = async () => {
    setSavingDetails(true);
    try {
      await api.put(`/events/${eventId}`, { ...details, complimentary_downloads: Number(details.complimentary_downloads) });
      toast.success("Details saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingDetails(false);
    }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    setUploadingCover(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/events/${eventId}/cover`, fd);
      toast.success("Cover updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploadingCover(false);
    }
  };

  const uploadFiles = (fileList) => {
    const vids = Array.from(fileList || []).filter((f) => f.type.startsWith("video/"));
    if (vids.length === 0) {
      toast.error("Please drop video files only");
      if (videoRef.current) videoRef.current.value = "";
      return;
    }
    if (!user?.upload_agreement_accepted) {
      setPendingFiles(vids);
      setAgreementChecked(false);
      setAgreementOpen(true);
      if (videoRef.current) videoRef.current.value = "";
      return;
    }
    startUpload(vids);
  };

  const startUpload = (vids) => {
    const count = uploadMgr.enqueue(vids, { eventId, price: bulkPrice || "0" });
    if (count > 0) toast.success(`${count} clip${count > 1 ? "s" : ""} added to uploads`);
    if (videoRef.current) videoRef.current.value = "";
  };

  const acceptAgreementAndUpload = async () => {
    setAcceptingAgreement(true);
    try {
      await api.post("/me/accept-upload-agreement");
      await checkAuth();
      setAgreementOpen(false);
      const files = pendingFiles;
      setPendingFiles(null);
      if (files) startUpload(files);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not record agreement");
    } finally {
      setAcceptingAgreement(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  const updateVideo = async (id, patch) => {
    try {
      await api.put(`/videos/${id}`, patch);
      setDrafts((d) => { const n = { ...d }; delete n[id]; return n; });
      flashSaved([id]);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const flashSaved = (ids) => {
    setFlashIds((prev) => Array.from(new Set([...prev, ...ids])));
    setTimeout(() => setFlashIds((prev) => prev.filter((x) => !ids.includes(x))), 1600);
  };

  const setDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));
  const draftFor = (v) => ({
    title: drafts[v.id]?.title ?? v.title,
    price: drafts[v.id]?.price ?? v.price,
  });
  const isDirty = (v) => {
    const d = drafts[v.id];
    if (!d) return false;
    return (d.title !== undefined && d.title !== v.title) ||
           (d.price !== undefined && Number(d.price) !== Number(v.price));
  };
  const dirtyCount = videos.filter(isDirty).length;

  const applyPriceToAll = () => {
    if (videos.length === 0) return;
    const price = bulkPrice === "" ? "0" : bulkPrice;
    setDrafts((d) => {
      const n = { ...d };
      videos.forEach((v) => { n[v.id] = { ...n[v.id], price }; });
      return n;
    });
    toast.success(`Applied $${Number(price).toFixed(2)} to all clips — review & save`);
  };

  const saveAll = async () => {
    const toSave = videos.filter(isDirty);
    if (toSave.length === 0) return;
    setSavingAll(true);
    try {
      await Promise.all(toSave.map((v) => {
        const d = draftFor(v);
        return api.put(`/videos/${v.id}`, { title: d.title, price: Number(d.price) });
      }));
      toast.success(`Saved ${toSave.length} clip${toSave.length > 1 ? "s" : ""}`);
      setDrafts({});
      flashSaved(toSave.map((v) => v.id));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingAll(false);
    }
  };

  const scheduleDelete = (items, label) => {
    const ids = items.map((it) => it.video.id);
    setVideos((prev) => prev.filter((v) => !ids.includes(v.id)));
    setSelectedIds((prev) => prev.filter((x) => !ids.includes(x)));
    const timeout = setTimeout(async () => {
      ids.forEach((id) => delete pendingDeletes.current[id]);
      try {
        await Promise.all(ids.map((id) => api.delete(`/videos/${id}`)));
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
        load();
      }
    }, 5000);
    items.forEach((it) => { pendingDeletes.current[it.video.id] = { timeout, video: it.video, index: it.index }; });
    toast(label, {
      action: {
        label: "Undo",
        onClick: () => {
          clearTimeout(timeout);
          ids.forEach((id) => delete pendingDeletes.current[id]);
          setVideos((prev) => {
            const next = [...prev];
            items.slice().sort((a, b) => a.index - b.index).forEach((it) => {
              next.splice(Math.min(it.index, next.length), 0, it.video);
            });
            return next;
          });
        },
      },
      duration: 5000,
    });
  };

  const deleteVideo = (id) => {
    const index = videos.findIndex((v) => v.id === id);
    if (index === -1) return;
    scheduleDelete([{ video: videos[index], index }], "Clip removed");
  };

  const toggleSelect = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const allSelected = videos.length > 0 && selectedIds.length === videos.length;
  const toggleSelectAll = () => setSelectedIds(allSelected ? [] : videos.map((v) => v.id));
  const exitSelectMode = () => { setSelectMode(false); setSelectedIds([]); };

  const applyPriceToSelected = async () => {
    if (selectedIds.length === 0) return;
    const price = Number(selectedPrice || 0);
    const ids = [...selectedIds];
    try {
      await Promise.all(ids.map((id) => api.put(`/videos/${id}`, { price })));
      toast.success(`Set $${price.toFixed(2)} on ${ids.length} clip${ids.length > 1 ? "s" : ""}`);
      flashSaved(ids);
      exitSelectMode();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteSelected = () => {
    if (selectedIds.length === 0) return;
    const items = selectedIds
      .map((id) => ({ video: videos.find((v) => v.id === id), index: videos.findIndex((v) => v.id === id) }))
      .filter((it) => it.video);
    const n = items.length;
    exitSelectMode();
    scheduleDelete(items, `${n} clip${n > 1 ? "s" : ""} removed`);
  };

  const handleDragStart = (i) => { dragIndex.current = i; };

  const handleDragEnter = (i) => {
    const from = dragIndex.current;
    if (from === null || from === i) return;
    setVideos((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      return next;
    });
    dragIndex.current = i;
  };

  const handleDrop = async () => {
    if (dragIndex.current === null) return;
    dragIndex.current = null;
    try {
      await api.post(`/events/${eventId}/videos/reorder`, { video_ids: videos.map((v) => v.id) });
      toast.success("Order saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      load();
    }
  };

  const publish = async () => {
    try {
      const { data } = await api.post(`/events/${eventId}/publish`);
      toast.success("Vault published");
      setEvent((prev) => ({ ...prev, published: true, share_slug: data.share_slug }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteEvent = async () => {
    setDeleting(true);
    try {
      await api.delete(`/events/${eventId}`);
      toast.success("Vault deleted");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setDeleting(false);
    }
  };

  const shareUrl = event?.share_slug ? `${window.location.origin}/vault/${event.share_slug}` : "";
  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading || !event) {
    return (
      <div className="min-h-screen bg-[#050505] grid place-items-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#00F0FF]" />
      </div>
    );
  }

  const a = event.analytics;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5 mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold">{event.title}</h1>
            <p className="text-white/50 mt-1">{a.video_count} clips · {event.published ? "Published" : "Draft"}</p>
          </div>
          <div className="flex items-center gap-3">
            {event.published ? (
              <>
                <Link
                  to={`/vault/${event.share_slug}`}
                  target="_blank"
                  data-testid="view-vault-btn"
                  className="inline-flex items-center gap-2 border border-white/20 rounded-full px-5 py-2.5 text-sm hover:bg-white/10 transition-colors"
                >
                  <ExternalLink className="h-4 w-4" /> View vault
                </Link>
                <button
                  onClick={publish}
                  data-testid="republish-btn"
                  className="inline-flex items-center gap-2 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded-full px-5 py-2.5 text-sm"
                >
                  <Check className="h-4 w-4" /> Live
                </button>
              </>
            ) : (
              <button
                onClick={publish}
                data-testid="publish-btn"
                className="inline-flex items-center gap-2 bg-[#00F0FF] text-black font-semibold rounded-full px-6 py-2.5 hover:scale-105 transition-transform duration-300 cyan-glow"
              >
                <Rocket className="h-4 w-4" /> Publish vault
              </button>
            )}
            <button
              onClick={() => setConfirmDelete(true)}
              data-testid="delete-vault-btn"
              title="Delete vault"
              className="inline-flex items-center gap-2 border border-white/15 text-white/60 rounded-full px-4 py-2.5 text-sm hover:bg-red-500/15 hover:text-red-400 hover:border-red-500/30 transition-colors"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          </div>
        </div>

        {/* Share link */}
        {event.published && (
          <div className="mb-8 rounded-2xl border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-[#00F0FF]">Private client link</p>
              <p data-testid="share-link" className="text-sm text-white/80 truncate mt-1">{shareUrl}</p>
            </div>
            <button
              onClick={copyLink}
              data-testid="copy-link-btn"
              className="inline-flex items-center gap-2 bg-[#00F0FF] text-black font-semibold rounded-full px-5 py-2.5 text-sm hover:scale-105 transition-transform duration-300"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied" : "Copy link"}
            </button>
          </div>
        )}

        {/* Analytics */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-12">
          <StatCard icon={DollarSign} label="Revenue" value={`$${a.total_revenue.toFixed(2)}`} accent="text-emerald-400" />
          <StatCard icon={Film} label="Videos sold" value={a.videos_sold} />
          <StatCard icon={Gift} label="Freebies used" value={a.complimentary_redeemed} />
          <StatCard icon={Eye} label="Total views" value={a.total_views} />
          <StatCard icon={Download} label="Downloads" value={a.total_downloads} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: details + cover */}
          <div className="space-y-6">
            <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-6">
              <h3 className="font-display text-lg font-semibold mb-4">Cover image</h3>
              <div className="relative aspect-video rounded-xl overflow-hidden bg-gradient-to-br from-[#141414] to-black mb-4">
                {event.cover_image_path ? (
                  <img src={`${BACKEND}/api/files/${event.cover_image_path}`} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full grid place-items-center text-white/20">
                    <ImageIcon className="h-10 w-10" />
                  </div>
                )}
              </div>
              <input
                ref={coverRef}
                type="file"
                accept="image/*"
                className="hidden"
                data-testid="cover-file-input"
                onChange={(e) => uploadCover(e.target.files?.[0])}
              />
              <button
                onClick={() => coverRef.current?.click()}
                disabled={uploadingCover}
                data-testid="upload-cover-btn"
                className="w-full inline-flex items-center justify-center gap-2 border border-white/15 rounded-full py-2.5 text-sm hover:bg-white/10 transition-colors disabled:opacity-60"
              >
                {uploadingCover ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {event.cover_image_path ? "Replace cover" : "Upload cover"}
              </button>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 space-y-4">
              <h3 className="font-display text-lg font-semibold">Event details</h3>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50">Title</label>
                <input
                  data-testid="edit-title"
                  value={details.title}
                  onChange={(e) => setDetails({ ...details, title: e.target.value })}
                  className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50">Description</label>
                <textarea
                  data-testid="edit-desc"
                  rows={3}
                  value={details.description}
                  onChange={(e) => setDetails({ ...details, description: e.target.value })}
                  className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF] resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-white/50">Date</label>
                  <input
                    type="date"
                    value={details.event_date}
                    onChange={(e) => setDetails({ ...details, event_date: e.target.value })}
                    className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-white/50">Location</label>
                  <input
                    value={details.location}
                    onChange={(e) => setDetails({ ...details, location: e.target.value })}
                    className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50">Complimentary downloads</label>
                <input
                  data-testid="edit-comp"
                  type="number"
                  min={0}
                  max={compMax}
                  value={details.complimentary_downloads}
                  onChange={(e) => {
                    let v = e.target.value;
                    if (compMax != null && Number(v) > compMax) v = String(compMax);
                    setDetails({ ...details, complimentary_downloads: v });
                  }}
                  className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]"
                />
                <p className="mt-1.5 text-xs text-white/40">
                  {compMax != null
                    ? `Up to ${compMax} on your ${user?.tier} plan`
                    : "Unlimited on your plan"}
                </p>
              </div>
              {canReorder && (
              <div className={`rounded-xl border p-4 transition-colors ${details.unlock_all ? "border-[#FFDD03]/40 bg-[#FFDD03]/5" : "border-white/10 bg-white/5"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <Gift className={`h-4 w-4 ${details.unlock_all ? "text-[#FFDD03]" : "text-white/50"}`} />
                      Unlock all clips
                    </p>
                    <p className="text-xs text-white/50 mt-1 leading-relaxed">
                      Grant clients full access to every clip — no purchase or free downloads needed. Use when they've paid you for the package independently.
                    </p>
                  </div>
                  <Switch
                    data-testid="unlock-all-toggle"
                    checked={details.unlock_all}
                    onCheckedChange={(v) => setDetails({ ...details, unlock_all: v })}
                  />
                </div>
              </div>
              )}
              <button
                onClick={saveDetails}
                disabled={savingDetails}
                data-testid="save-details-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 rounded-full py-2.5 text-sm font-medium transition-colors disabled:opacity-60"
              >
                {savingDetails ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save details
              </button>
            </div>
          </div>

          {/* Right: videos */}
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-6">
              <div className="flex items-center justify-between mb-4 gap-4">
                <h3 className="font-display text-lg font-semibold">Upload clips</h3>
                <div className="flex items-center gap-2">
                  <div className="relative w-40">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                    <input
                      data-testid="bulk-price-input"
                      type="number"
                      min={0}
                      step="1"
                      value={bulkPrice}
                      onChange={(e) => setBulkPrice(e.target.value)}
                      placeholder="Price / clip"
                      title="Price applied to each uploaded clip"
                      className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]"
                    />
                  </div>
                  {videos.length > 0 && (
                    <button
                      onClick={applyPriceToAll}
                      data-testid="apply-price-all-btn"
                      title={`Set every existing clip to $${Number(bulkPrice || 0).toFixed(2)}`}
                      className="shrink-0 whitespace-nowrap border border-white/15 rounded-xl px-3 py-2.5 text-sm text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                    >
                      Apply to all
                    </button>
                  )}
                </div>
              </div>

              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => videoRef.current?.click()}
                data-testid="dropzone"
                className={`cursor-pointer rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
                  dragOver ? "border-[#00F0FF] bg-[#00F0FF]/5" : "border-white/15 hover:border-white/30"
                }`}
              >
                <div className="flex flex-col items-center gap-3">
                  <span className="grid place-items-center h-12 w-12 rounded-xl bg-[#00F0FF]/10 text-[#00F0FF]">
                    <Upload className="h-6 w-6" />
                  </span>
                  <p className="text-sm font-medium">Drag & drop your clips here</p>
                  <p className="text-xs text-white/40">
                    or click to browse · uploads run in the background · filenames become titles
                  </p>
                </div>
              </div>

              <input
                ref={videoRef}
                type="file"
                accept="video/*"
                multiple
                data-testid="video-file-input"
                onChange={(e) => uploadFiles(e.target.files)}
                className="hidden"
              />
              <p className="mt-3 text-xs text-white/40">
                Each clip is priced at ${Number(bulkPrice || 0).toFixed(2)} — track progress in the Upload Manager (bottom-right). Edit titles &amp; prices below.
              </p>
            </div>

            <div className="space-y-3">
              {videos.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-white/15 py-16 text-center text-white/40">
                  <Video className="h-8 w-8 mx-auto mb-3" />
                  No clips uploaded yet.
                </div>
              ) : (
                <>
                  {videos.length > 0 && (
                    <div className="flex flex-col gap-3 px-1 pb-1">
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-2 text-xs text-white/40">
                        {selectMode ? (
                          <span>Select clips to update or remove together.</span>
                        ) : videos.length > 1 ? (canReorder ? (
                          <>
                            <GripVertical className="h-3.5 w-3.5" />
                            Drag clips by the handle to reorder how clients see them.
                          </>
                        ) : (
                          <>
                            <Lock className="h-3.5 w-3.5 text-[#FFDD03]" />
                            Drag-to-reorder is a paid-plan feature.{" "}
                            <Link to="/dashboard" className="text-[#FFDD03] hover:underline">Upgrade to reorder</Link>
                          </>
                        )) : null}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          {dirtyCount > 0 && !selectMode && (
                            <>
                              <button
                                onClick={() => setDrafts({})}
                                disabled={savingAll}
                                data-testid="discard-changes-btn"
                                className="text-xs text-white/50 hover:text-white transition-colors disabled:opacity-40"
                              >
                                Discard changes
                              </button>
                              <button
                                onClick={saveAll}
                                disabled={savingAll}
                                data-testid="save-all-videos-btn"
                                className="inline-flex items-center gap-2 bg-[#00F0FF] text-black font-semibold rounded-full px-4 py-2 text-xs hover:scale-105 transition-transform duration-300 cyan-glow disabled:opacity-60 disabled:hover:scale-100"
                              >
                                {savingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                                Save all changes ({dirtyCount})
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
                            data-testid="select-mode-toggle"
                            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition-colors ${
                              selectMode
                                ? "bg-white/10 text-white hover:bg-white/15"
                                : "border border-white/15 text-white/70 hover:bg-white/10 hover:text-white"
                            }`}
                          >
                            {selectMode ? (<><X className="h-3.5 w-3.5" /> Cancel</>) : (<><CheckSquare className="h-3.5 w-3.5" /> Select</>)}
                          </button>
                        </div>
                      </div>

                      {selectMode && (
                        <div
                          data-testid="selection-toolbar"
                          className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-3"
                        >
                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                data-testid="select-all-clips"
                                checked={allSelected}
                                onChange={toggleSelectAll}
                                className="h-4 w-4 rounded border-white/20 bg-white/5 accent-[#00F0FF] cursor-pointer"
                              />
                              Select all
                            </label>
                            <span data-testid="selected-count" className="text-sm text-white/60">
                              {selectedIds.length} selected
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="relative w-28">
                              <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
                              <input
                                data-testid="selected-price-input"
                                type="number"
                                min={0}
                                step="1"
                                value={selectedPrice}
                                onChange={(e) => setSelectedPrice(e.target.value)}
                                className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-2 py-2 text-sm focus:outline-none focus:border-[#00F0FF]"
                              />
                            </div>
                            <button
                              onClick={applyPriceToSelected}
                              disabled={selectedIds.length === 0}
                              data-testid="apply-price-selected-btn"
                              className="inline-flex items-center gap-1.5 bg-[#00F0FF] text-black font-semibold rounded-full px-4 py-2 text-xs hover:scale-105 transition-transform duration-300 disabled:opacity-40 disabled:hover:scale-100"
                            >
                              <DollarSign className="h-3.5 w-3.5" /> Set price
                            </button>
                            <button
                              onClick={deleteSelected}
                              disabled={selectedIds.length === 0}
                              data-testid="delete-selected-btn"
                              className="inline-flex items-center gap-1.5 border border-red-500/30 text-red-400 rounded-full px-4 py-2 text-xs hover:bg-red-500/15 transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
                            >
                              <Trash2 className="h-3.5 w-3.5" /> Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {videos.map((v, i) => (
                    <VideoRow
                      key={v.id}
                      v={v}
                      index={i}
                      canReorder={canReorder && !selectMode}
                      draft={draftFor(v)}
                      dirty={isDirty(v)}
                      flash={flashIds.includes(v.id)}
                      selectMode={selectMode}
                      selected={selectedIds.includes(v.id)}
                      onToggleSelect={() => toggleSelect(v.id)}
                      onDraftChange={(patch) => setDraft(v.id, patch)}
                      onUpdate={updateVideo}
                      onDelete={deleteVideo}
                      onPreview={setPreview}
                      onDragStart={() => handleDragStart(i)}
                      onDragEnter={() => handleDragEnter(i)}
                      onDrop={handleDrop}
                    />
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Clip preview modal */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl bg-black border-white/10 p-0 overflow-hidden">
          <button
            onClick={() => setPreview(null)}
            data-testid="close-preview-btn"
            className="absolute top-3 right-3 z-20 grid place-items-center h-9 w-9 rounded-full bg-black/60 backdrop-blur-md text-white hover:bg-white/20 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
          {preview && (
            <div>
              <DialogTitle className="sr-only">{preview.title}</DialogTitle>
              <DialogDescription className="sr-only">Preview of {preview.title}</DialogDescription>
              <video
                key={preview.id}
                data-testid="preview-player"
                src={`${BACKEND}/api/videos/${preview.id}/preview`}
                controls
                autoPlay
                className="w-full aspect-video bg-black"
              />
              <div className="p-5">
                <h3 className="font-display text-lg font-semibold">{preview.title}</h3>
                <p className="text-xs text-white/40 mt-0.5">Creator preview · ${Number(preview.price).toFixed(2)}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete vault confirmation */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="bg-[#0d0d0d] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Delete this vault?</DialogTitle>
            <DialogDescription className="text-white/50">
              This permanently removes “{event.title}”, all its clips, and any client access. This can't be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <button
              onClick={() => setConfirmDelete(false)}
              className="rounded-full px-5 py-2.5 text-sm bg-white/10 hover:bg-white/15 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={deleteEvent}
              disabled={deleting}
              data-testid="confirm-delete-vault-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-60"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete vault
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={agreementOpen} onOpenChange={(o) => { if (!acceptingAgreement) setAgreementOpen(o); }}>
        <DialogContent className="bg-[#0d0d0d] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Upload Agreement</DialogTitle>
            <DialogDescription className="text-white/50">
              Before uploading, please confirm you have the rights to the content.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-white/60 leading-relaxed space-y-2 max-h-[45vh] overflow-y-auto pr-1">
            <p>By checking the box below and uploading content, you agree that:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>You own the content you upload, or have all necessary rights, licenses, and permissions to upload and distribute it — including permission and releases from clients and any individuals appearing in the footage, where applicable.</li>
              <li>Your uploads do not infringe any copyright, trademark, privacy, publicity, or other legal rights of any third party.</li>
              <li>You are solely responsible for the content you upload.</li>
              <li>You will not upload illegal, harmful, defamatory, or malicious content.</li>
              <li>You grant Narrative Vault a limited license to store, process, and display your content solely to operate and provide the service.</li>
              <li>Narrative Vault does not claim ownership of your content.</li>
              <li>Narrative Vault may remove content that violates its policies or applicable law.</li>
            </ul>
          </div>
          <label className="flex items-start gap-3 text-sm text-white/70 leading-relaxed cursor-pointer select-none mt-1">
            <input
              data-testid="upload-agreement-checkbox"
              type="checkbox"
              checked={agreementChecked}
              onChange={(e) => setAgreementChecked(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/20 bg-white/5 accent-[#00F0FF] cursor-pointer"
            />
            <span>
              I have read and agree to the{" "}
              <Link to="/terms" target="_blank" className="text-[#00F0FF] hover:underline">Terms of Service</Link>,{" "}
              <Link to="/privacy" target="_blank" className="text-[#00F0FF] hover:underline">Privacy Policy</Link>, and{" "}
              <Link to="/copyright" target="_blank" className="text-[#00F0FF] hover:underline">Copyright Policy</Link>.
            </span>
          </label>
          <DialogFooter className="gap-2 sm:gap-2">
            <button
              onClick={() => { if (!acceptingAgreement) { setAgreementOpen(false); setPendingFiles(null); } }}
              className="rounded-full px-5 py-2.5 text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>
            <button
              data-testid="upload-agreement-accept"
              onClick={acceptAgreementAndUpload}
              disabled={!agreementChecked || acceptingAgreement}
              className="inline-flex items-center justify-center gap-2 bg-[#00F0FF] text-black font-semibold rounded-full px-6 py-2.5 text-sm hover:scale-105 transition-transform duration-300 disabled:opacity-40 disabled:hover:scale-100"
            >
              {acceptingAgreement && <Loader2 className="h-4 w-4 animate-spin" />}
              Agree &amp; Upload
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VideoRow({ v, index, canReorder, draft, dirty, flash, selectMode, selected, onToggleSelect, onDraftChange, onUpdate, onDelete, onPreview, onDragStart, onDragEnter, onDrop }) {
  const title = draft.title;
  const price = draft.price;

  return (
    <div
      data-testid={`video-row-${v.id}`}
      onDragEnter={canReorder ? onDragEnter : undefined}
      onDragOver={canReorder ? (e) => e.preventDefault() : undefined}
      className={`rounded-2xl border p-4 flex flex-col sm:flex-row sm:items-center gap-4 transition-colors duration-500 ${
        selectMode && selected
          ? "border-[#00F0FF]/60 bg-[#00F0FF]/10"
          : flash
          ? "border-emerald-400/70 bg-emerald-400/10"
          : dirty
          ? "border-[#00F0FF]/60 bg-[#00F0FF]/5"
          : "border-white/10 bg-[#0d0d0d]"
      }`}
    >
      {selectMode && (
        <label className="grid place-items-center shrink-0 cursor-pointer">
          <input
            type="checkbox"
            data-testid={`select-clip-${v.id}`}
            checked={selected}
            onChange={onToggleSelect}
            className="h-5 w-5 rounded border-white/20 bg-white/5 accent-[#00F0FF] cursor-pointer"
          />
        </label>
      )}
      {canReorder && (
        <button
          draggable
          onDragStart={onDragStart}
          onDragEnd={onDrop}
          data-testid={`drag-handle-${v.id}`}
          title="Drag to reorder"
          className="hidden sm:grid place-items-center h-9 w-6 shrink-0 cursor-grab active:cursor-grabbing text-white/30 hover:text-white/70 transition-colors"
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <button
        onClick={() => onPreview(v)}
        data-testid={`preview-video-${v.id}`}
        title="Preview clip"
        className="group grid place-items-center h-16 w-28 shrink-0 rounded-lg bg-gradient-to-br from-[#1a1a1a] to-black text-[#00F0FF]/60 relative overflow-hidden"
      >
        {v.thumbnail_path ? (
          <img src={`${BACKEND}/api/files/${v.thumbnail_path}`} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <Film className="h-6 w-6" />
        )}
        <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center">
          <Play className="h-6 w-6 text-white" fill="currentColor" />
        </span>
        <span className="absolute top-1 left-1.5 text-[10px] text-white/60 z-10 bg-black/40 rounded px-1">#{index + 1}</span>
      </button>
      <div className="flex-1 grid grid-cols-1 sm:grid-cols-[1fr,130px] gap-3">
        <input
          data-testid={`video-title-${v.id}`}
          value={title}
          onChange={(e) => onDraftChange({ title: e.target.value })}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#00F0FF]"
        />
        <div className="relative">
          <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
          <input
            data-testid={`video-price-${v.id}`}
            type="number"
            min={0}
            step="1"
            value={price}
            onChange={(e) => onDraftChange({ price: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-2 py-2 text-sm focus:outline-none focus:border-[#00F0FF]"
          />
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs text-white/40">
        <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" />{v.views}</span>
        <span className="inline-flex items-center gap-1"><Download className="h-3.5 w-3.5" />{v.downloads}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPreview(v)}
          data-testid={`preview-btn-${v.id}`}
          className="grid place-items-center h-9 w-9 rounded-lg bg-white/5 text-white/60 hover:bg-white/15 hover:text-white transition-colors"
          title="Preview clip"
        >
          <Play className="h-4 w-4" />
        </button>
        <button
          onClick={() => onUpdate(v.id, { title, price: Number(price) })}
          disabled={!dirty}
          data-testid={`save-video-${v.id}`}
          className="grid place-items-center h-9 w-9 rounded-lg bg-[#00F0FF]/10 text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black transition-colors disabled:opacity-30 disabled:hover:bg-[#00F0FF]/10 disabled:hover:text-[#00F0FF]"
          title={dirty ? "Save changes" : "No changes to save"}
        >
          <Save className="h-4 w-4" />
        </button>
        <button
          onClick={() => onDelete(v.id)}
          data-testid={`delete-video-${v.id}`}
          className="grid place-items-center h-9 w-9 rounded-lg bg-white/5 text-white/50 hover:bg-red-500/20 hover:text-red-400 transition-colors"
          title="Delete"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
